/**
 * Renders the OCI dashboard at the connector source-record boundary.
 *
 * Source record summaries stay lightweight and paged. Attachment findings are
 * exposed only for the expanded record so Award, Subaward, and Negotiation
 * packages share one review workflow without eager payload requests.
 */
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Play,
} from 'lucide-react'
import { Fragment } from 'react'

import {
  Badge,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui'

import { OciActivityBadge, OciStatusBadge } from './OciDashboardIndicators'
import type {
  OciRecordSummary,
  OciRecordSortKey,
  OciDashboardRow,
  OciFeedActivity,
  SortDirection,
} from './ociDashboardModel'

const shortDateTime = (value: string) => new Date(value).toLocaleString(undefined, {
  dateStyle: 'short',
  timeStyle: 'short',
})

function SortButton({
  label,
  sortKey,
  activeKey,
  direction,
  onSort,
}: {
  label: string
  sortKey: OciRecordSortKey
  activeKey: OciRecordSortKey
  direction: SortDirection
  onSort: (key: OciRecordSortKey) => void
}) {
  const Icon = activeKey !== sortKey ? ArrowUpDown : direction === 'asc' ? ArrowUp : ArrowDown
  return (
    <Button
      variant="ghost"
      size="sm"
      className="-ml-3 h-8"
      aria-label={`Sort by ${label}`}
      onClick={() => onSort(sortKey)}
    >
      {label}<Icon className="ml-1 h-3.5 w-3.5" />
    </Button>
  )
}

function ExpandedAttachments({
  rows,
  recordTypes,
  expandedRecordType,
  dataLoading,
  dataError,
  activity,
  onToggleRecordType,
  onOpen,
}: {
  rows: OciDashboardRow[]
  recordTypes: OciRecordSummary['recordTypes']
  expandedRecordType: string | null
  dataLoading: boolean
  dataError: boolean
  activity?: OciFeedActivity
  onToggleRecordType: (recordType: string) => void
  onOpen: (row: OciDashboardRow) => void
}) {
  return (
    <div className="space-y-2">
      {recordTypes
        .map(({ recordType, findingCount }) => {
          const expanded = expandedRecordType === recordType
          const typeRows = rows.filter(row => (
            (row.recordType ?? 'Document') === recordType
          ))
          return (
            <div
              key={recordType}
              className="overflow-hidden rounded-md border bg-background"
              data-testid={`oci-record-type-panel-${recordType}`}
            >
              <button
                type="button"
                className="flex w-full items-center gap-2 px-4 py-3 text-left"
                aria-expanded={expanded}
                aria-label={`${expanded ? 'Collapse' : 'Expand'} ${recordType}`}
                onClick={() => onToggleRecordType(recordType)}
              >
                <ChevronRight className={`h-4 w-4 transition-transform ${expanded ? 'rotate-90' : ''}`} />
                <Badge variant="secondary">{recordType}</Badge>
                {findingCount > 0 ? (
                  <Badge
                    variant="destructive"
                    className="ml-auto"
                    data-testid={`oci-record-type-reported-${recordType}`}
                  >
                    {findingCount} reported
                  </Badge>
                ) : null}
              </button>
              {expanded && (
                <div className="divide-y border-t">
                  {dataLoading ? (
                    <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading data…
                    </div>
                  ) : dataError ? (
                    <p className="py-8 text-center text-sm text-destructive">
                      Data could not be loaded. Collapse and reopen this section to retry.
                    </p>
                  ) : typeRows.map(row => (
                    <div
                      key={row.documentId}
                      className="grid gap-x-4 gap-y-2 px-4 py-3 text-sm md:grid-cols-[minmax(0,1fr)_auto] md:items-center xl:grid-cols-[minmax(0,1fr)_minmax(14rem,24rem)_auto_10rem]"
                      data-testid={`oci-record-attachment-${row.documentId}`}
                    >
                      <div className="min-w-0">
                        <p className="truncate font-medium">{row.documentName}</p>
                        <p className="truncate font-mono text-xs text-muted-foreground">
                          {row.documentId} · v{row.version}
                        </p>
                      </div>
                      <div className="flex min-w-0 flex-wrap gap-1 md:justify-end xl:justify-start">
                        {row.findingsState === 'loading' ? (
                          <span className="flex items-center text-muted-foreground">
                            <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> Loading findings…
                          </span>
                        ) : row.findingsState === 'error' ? (
                          <span className="text-destructive">Findings unavailable</span>
                        ) : row.categories.length ? (
                          row.categories.map(category => <Badge key={category} variant="outline">{category}</Badge>)
                        ) : (
                          <span className="text-muted-foreground">No OCI category</span>
                        )}
                      </div>
                      <div className="md:justify-self-start xl:justify-self-auto">
                        {row.findingsState === 'loading' ? (
                          <Badge variant="secondary">{row.reportedFindingCount} reported</Badge>
                        ) : row.findingsState === 'error' ? (
                          <Badge variant="secondary">Unavailable</Badge>
                        ) : (
                          <OciStatusBadge
                            status={row.status}
                            count={row.findingCount}
                            documentName={row.documentName}
                            activity={activity}
                            onOpen={() => onOpen(row)}
                          />
                        )}
                      </div>
                      <span className="whitespace-nowrap text-muted-foreground md:justify-self-end xl:text-right">
                        {row.updatedAt ? shortDateTime(row.updatedAt) : 'Not analyzed'}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )
        })}
    </div>
  )
}

function RecordAnalysisStatus({
  group,
  activity,
}: {
  group: OciRecordSummary
  activity?: OciFeedActivity
}) {
  // The connector only imports records. This column reports the separate,
  // user-started analysis state across every attachment in the award.
  if (
    activity
    && group.analyzedAttachmentCount < group.attachmentCount
  ) {
    return <OciActivityBadge activity={activity} />
  }
  if (group.status === 'error') return <Badge variant="destructive">Run failed</Badge>
  if (group.status === 'not-run') return <Badge variant="secondary">Not analyzed</Badge>
  if (group.status === 'stale') {
    return <Badge className="bg-amber-100 text-amber-900 hover:bg-amber-100">Needs rerun</Badge>
  }
  return <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">Analyzed</Badge>
}

function ReportedFindings({ group }: { group: OciRecordSummary }) {
  if (group.findingCount > 0) {
    return <Badge variant="destructive">{group.findingCount} reported</Badge>
  }
  if (group.status === 'clear') return <Badge variant="secondary">None reported</Badge>
  return (
    <Badge variant={group.status === 'error' ? 'destructive' : 'secondary'}>
      Unavailable
    </Badge>
  )
}

export function OciRecordTable({
  groups,
  expandedRecordKey,
  expandedRecordType,
  expandedRows,
  activityByFeedId,
  dataLoading,
  dataError,
  sortKey,
  direction,
  pageIndex,
  pageCount,
  totalRecords,
  pageSize,
  onSort,
  onToggle,
  onToggleRecordType,
  onOpenRow,
  preparingRecordKey,
  onRun,
  onPageChange,
}: {
  groups: OciRecordSummary[]
  expandedRecordKey: string | null
  expandedRecordType: string | null
  expandedRows: OciDashboardRow[]
  activityByFeedId: ReadonlyMap<string, OciFeedActivity>
  dataLoading: boolean
  dataError: boolean
  sortKey: OciRecordSortKey
  direction: SortDirection
  pageIndex: number
  pageCount: number
  totalRecords: number
  pageSize: number
  onSort: (key: OciRecordSortKey) => void
  onToggle: (key: string) => void
  onToggleRecordType: (recordType: string) => void
  onOpenRow: (row: OciDashboardRow) => void
  preparingRecordKey: string | null
  onRun: (group: OciRecordSummary) => void
  onPageChange: (page: number) => void
}) {
  const firstRecord = totalRecords === 0 ? 0 : pageIndex * pageSize + 1
  const lastRecord = Math.min(totalRecords, firstRecord + groups.length - 1)

  return (
    <>
      <Table className="w-full table-fixed">
        <TableHeader>
          <TableRow>
            <TableHead className="w-10"><span className="sr-only">Details</span></TableHead>
            <TableHead className="w-full max-w-0"><SortButton label="Award" sortKey="record" activeKey={sortKey} direction={direction} onSort={onSort} /></TableHead>
            <TableHead className="hidden w-36 whitespace-nowrap md:table-cell">Synced</TableHead>
            <TableHead className="w-44 whitespace-nowrap">Analysis</TableHead>
            <TableHead className="w-28 whitespace-nowrap"><SortButton label="Reported" sortKey="findings" activeKey={sortKey} direction={direction} onSort={onSort} /></TableHead>
            <TableHead className="hidden w-36 whitespace-nowrap lg:table-cell"><SortButton label="Updated" sortKey="updated" activeKey={sortKey} direction={direction} onSort={onSort} /></TableHead>
            <TableHead className="w-24 text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {groups.map(group => {
            const expanded = expandedRecordKey === group.key
            const recordLabel = group.recordName ?? 'No source record'
            const activity = group.feedId
              ? activityByFeedId.get(group.feedId)
              : undefined
            return (
              <Fragment key={group.key}>
                <TableRow data-testid={`oci-record-${group.key}`}>
                  <TableCell className="align-middle">
                    <Button
                      size="icon"
                      variant="ghost"
                      className="size-8"
                      data-testid={`oci-record-toggle-${group.key}`}
                      aria-label={`${expanded ? 'Collapse' : 'Expand'} award ${recordLabel}`}
                      aria-expanded={expanded}
                      onClick={() => onToggle(group.key)}
                    >
                      {expanded
                        ? <ChevronDown className="h-4 w-4" />
                        : <ChevronRight className="h-4 w-4" />}
                    </Button>
                  </TableCell>
                  <TableCell className="w-full max-w-0">
                    <div className="min-w-0">
                      <span className="block truncate font-mono text-sm font-medium">{recordLabel}</span>
                      {group.sourceDisplayFields.length > 0 ? (
                        <div
                          className="mt-0.5 truncate text-xs text-muted-foreground"
                          data-testid={`oci-record-source-fields-${group.key}`}
                        >
                          {group.sourceDisplayFields.map((field, index) => (
                            <Fragment key={field.key}>
                              {index > 0 ? ' · ' : null}{field.label}: {field.value}
                            </Fragment>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  </TableCell>
                  <TableCell className="hidden whitespace-nowrap md:table-cell">
                    <div>{shortDateTime(group.syncedAt)}</div>
                    <div className="text-xs text-muted-foreground">by {group.syncedBy ?? '—'}</div>
                  </TableCell>
                  <TableCell data-testid="oci-record-analysis-status">
                    <RecordAnalysisStatus group={group} activity={activity} />
                  </TableCell>
                  <TableCell className="whitespace-nowrap" data-testid="oci-record-findings-status">
                    <ReportedFindings group={group} />
                  </TableCell>
                  <TableCell className="hidden whitespace-nowrap text-muted-foreground lg:table-cell">
                    {group.updatedAt ? shortDateTime(group.updatedAt) : 'Not analyzed'}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={preparingRecordKey !== null}
                      aria-label={`Run analysis for award ${recordLabel}`}
                      onClick={() => onRun(group)}
                    >
                      {preparingRecordKey === group.key
                        ? <Loader2 className="h-4 w-4 animate-spin" />
                        : <Play className="h-4 w-4" />}
                      {group.status === 'clear' || group.status === 'flagged' ? 'Rerun' : 'Run'}
                    </Button>
                  </TableCell>
                </TableRow>
                {expanded && (
                  <TableRow>
                    <TableCell colSpan={7} className="border-t bg-muted/30 p-4">
                      <ExpandedAttachments
                        rows={expandedRows}
                        recordTypes={group.recordTypes}
                        expandedRecordType={expandedRecordType}
                        dataLoading={dataLoading}
                        dataError={dataError}
                        activity={activity}
                        onToggleRecordType={onToggleRecordType}
                        onOpen={onOpenRow}
                      />
                    </TableCell>
                  </TableRow>
                )}
              </Fragment>
            )
          })}
        </TableBody>
      </Table>
      <div className="flex flex-wrap items-center justify-between gap-3 border-t px-4 py-3 text-sm text-muted-foreground">
        <span>
          Showing {firstRecord}–{lastRecord} of {totalRecords} award{totalRecords === 1 ? '' : 's'}
        </span>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            disabled={pageIndex === 0}
            onClick={() => onPageChange(pageIndex - 1)}
          >
            <ChevronLeft className="mr-1 h-4 w-4" /> Previous
          </Button>
          <span className="px-2">Page {pageIndex + 1} of {pageCount}</span>
          <Button
            variant="ghost"
            size="sm"
            disabled={pageIndex >= pageCount - 1}
            onClick={() => onPageChange(pageIndex + 1)}
          >
            Next <ChevronRight className="ml-1 h-4 w-4" />
          </Button>
        </div>
      </div>
    </>
  )
}
